import {
  AfterViewInit,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  QuarterSelectorType,
  QuarterSelectorValue,
  DefaultSelectionValue,
  FilterInputType,
  FormGroupValue,
  InputProps,
  InputPropsEnum,
  Placeholder,
  INDEXED_DB_CONSTANTS,
  DataObject,
} from '@miq/shared-models';
import { L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { of } from 'rxjs';
import { SimpleFormGroupService } from './simple-form-group.service';
import {
  UserInfoResolver,
  DialogService,
  LookupRefService,
  IndexedDbService,
  getIsDetailPageEnabled,
  MORE_FILTERS_DIALOG,
  TIM_FILTERS_SINK,
} from '@miq/platform';
import { filter as filterOperator, map } from 'rxjs/operators';
import { filtersSieve } from '../../utils/valid-filters';
import { DOCUMENT, NgClass, UpperCasePipe, TitleCasePipe } from '@angular/common';
import { GoBackLinkComponent } from '../go-back-link/go-back-link.component';
import { SimpleSelectComponent } from '../simple-select/simple-select.component';
import { SearchableMultiSelectComponent } from '../searchable-multi-select/searchable-multi-select.component';
import { PeriodRangeComponent } from './period-range/period-range.component';
import { RangeItemComponent } from '../range-item/range-item.component';
import { isCELocale } from '@miq/utils';
import { EqualsFilterComponent } from './equals-filter/equals-filter.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { AutoCompleteComponent } from './auto-complete/auto-complete.component';
import { filterTypes, isPostalCodeFilter, preserveFilterKeyIds, preserveFilterKeys } from './filters.utils';
import { CECountryRangeItemComponent } from '../range-item/ce-range-item.component';
// L2-S5:
//  - `getIsDetailPageEnabled` is the no-arg reader from @miq/platform's MIQ 2.0 flag cache. The
//    host's `getIsDetailPageEnabled(sharedService)` overload returns
//    `sharedService.isDetailPageEnabled()`, and `SharedService` (providedIn: 'root', so a single
//    instance) sets that field ONLY through `setIsDetailPageEnabled`, which writes the very same
//    cache this reader consumes. The two are value-identical, including before the host has set
//    the flag (`false` both ways). Reading the cache drops the `SharedService` injection.
//  - `MORE_FILTERS_DIALOG` replaces the direct `MoreFiltersDialogWrapperComponent` import: the
//    class was pure payload for `DialogService.openDialog`, with zero binding surface.
//  - `TIM_FILTERS_SINK` replaces the `ViewSpacesFromTimService` injection; see the token for the
//    analysis of `updateNewFilters` and why the call sites are now gated on `type()`.

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-filters-bar',
  templateUrl: './filters-bar.component.html',
  styleUrl: './filters-bar.component.scss',
  imports: [
    FormsModule,
    NgClass,
    ReactiveFormsModule,
    GoBackLinkComponent,
    SimpleSelectComponent,
    SearchableMultiSelectComponent,
    PeriodRangeComponent,
    RangeItemComponent,
    CECountryRangeItemComponent,
    EqualsFilterComponent,
    SearchBarComponent,
    AutoCompleteComponent,
    UpperCasePipe,
    TitleCasePipe,
    L10nTranslatePipe,
  ],
})
export class FiltersBarComponent<T = DefaultSelectionValue> implements OnDestroy, OnInit, AfterViewInit {
  readonly CENumberFormat = isCELocale();
  protected destroyRef = inject(DestroyRef);
  addBackLink = input(false);
  // TODO l10n: figure how to translate options (if needed) and labels
  readonly filtersInput = input<Array<InputProps<T>>>([], { alias: 'filters' });
  filters: Array<InputProps<T>> = [];
  backLinkRoute = input<string[]>([]);
  isDownloadReportList = input(false);
  reportType = input<string>();
  disableAll = input(false);
  type = input('');
  selection = output<FormGroupValue<T>>();
  // emit individual changes for cases when specific fields triggers custom events
  change = output<{ [key: string]: any }>();
  formReady = output<FormGroup>();
  clearFilters = output<any>();
  updateView = output<any>();
  showFilterActions = input(true);
  whiteBackground = input(false);
  fixedFilters = input([]);
  defaultAvailabilityFilterIds = input<number[]>([]);
  form: FormGroup;
  filterInputTypeEnum = FilterInputType; // enum assignment for template usage
  reportId: number;
  currentQtdValue: number;
  marketQtdValue: number;
  periodRangeValue: any;
  periodType = signal<string>(QuarterSelectorType.Quarter);
  maxValue: QuarterSelectorValue | Date = new Date();
  userId;
  baseFilters;
  resetButtonIsEnabled = true;
  userSelectedFiltrs = []; // to store all filters from the dialog
  originalFilters: Array<InputProps<T>> = []; // to store original filters for translation
  isDetailPageEnabled: boolean = false;
  get isReportsUrl(): boolean {
    const pathname = this.document.defaultView?.location.pathname ?? this.router.url.split(/[?#]/, 1)[0];
    return pathname.toLowerCase().split('/').includes('reports');
  }
  filterTypes = filterTypes;
  private readonly fb = inject(FormBuilder);
  private readonly simpleFormGroupService = inject(SimpleFormGroupService);
  public translationService = inject(L10nTranslationService);
  private readonly route = inject(ActivatedRoute);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private readonly userInfoResolver = inject(UserInfoResolver);
  private readonly notificationService = inject(DialogService);
  private readonly lookupService = inject(LookupRefService);
  /** See {@link TIM_FILTERS_SINK}. Optional: absent under a standalone remote serve. */
  private readonly timFiltersSink = inject(TIM_FILTERS_SINK, { optional: true });
  private readonly indexedDbService = inject(IndexedDbService);
  /** See {@link MORE_FILTERS_DIALOG}. Optional: absent under a standalone remote serve. */
  private readonly moreFiltersDialog = inject(MORE_FILTERS_DIALOG, { optional: true });
  private lastProcessedFiltersInput: unknown = null;
  constructor() {
    effect(() => {
      const v = this.filtersInput();
      if (v === undefined || v === this.lastProcessedFiltersInput) {
        return;
      }
      this.lastProcessedFiltersInput = v;
      this.filters = v;
      this.initForm();
      this.filters = this.filters.map(filter => {
        return {
          ...filter,
          label:
            filter.type === FilterInputType.Range
              ? filter.isLabelTranslated
                ? filter.label
                : this.translationService.translate(filter.label)
              : filter.label,
        };
      });
    });
  }
  ngOnInit() {
    this.isDetailPageEnabled = getIsDetailPageEnabled();
    /* required by translation */
    if (!this.form && this.filtersInput()?.length) {
      this.filters = this.filtersInput();
      this.initForm();
    }
    this.originalFilters = JSON.parse(JSON.stringify(this.filters));
    this.reportId = this.route.snapshot.params.idReport;
    if (this.form?.controls.markets) {
      this.marketQtdValue = Number(this.form.controls.markets.value);
      this.periodRangeValue = this.form.controls.periodRange?.value;
    }
    this.userId = this.userInfoResolver.resolve().id;
    this.lookupService.getFilters().subscribe(filters => (this.baseFilters = filters));
  }
  ngAfterViewInit() {
    // Load persisted filters from IndexedDB after view is initialized
    this.loadPersistedFilters();
  }
  ngOnDestroy() {
    /* REQUIRED by untilDestroyed and translation */
  }
  /**
   * Get primary identifier based on component type
   * For 'searchAvailFromTim': use key || field
   * For others: use field only
   */
  private getPrimaryIdentifier(filterItem: InputProps<T>): string | number {
    if (this.type() === this.filterTypes.availFromTim) {
      return filterItem.key || filterItem.field;
    }
    return filterItem.field;
  }
  backArrow() {
    // no-op: back navigation for this bar is not yet implemented
  }
  initForm() {
    this.form = this.fb.group(this.simpleFormGroupService.makeGroup<T>(this.inputs));
    this.periodType.set(this.form.controls.periodType && this.form.controls.periodType.value);
    const today = new Date();
    if (this.periodType() === QuarterSelectorType.Yearly) {
      today.setFullYear(today.getFullYear() - 1);
    }
    this.maxValue = today;
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: FormGroupValue<T>) => this.selection.emit(value));
    this.formReady.emit(this.form);
  }
  emitChangeAndPatchForm(change: { [key: string]: any }) {
    /**
     * If the change has targetField, it means it's a cascade change
     * So we need to handle it differently
     * Example: Retail Sector -> Retail Subsector
     *
     * This will be triggered only initially when the parent filter has a default value
     * The child filter will be updated based on the parent filter value
     * The child filter will not emit the change event, as the parent filter will emit the event and the child filter will be updated accordingly
     *
     */
    if (change.hasOwnProperty(InputPropsEnum.targetField)) {
      this.applyCascadeChanges(change);
      return;
    }
    this.form.patchValue(change);
    if (change && change.markets != undefined) {
      this.marketQtdValue = Number(change.markets);
      if (this.form.controls.subMarket) {
        this.form.controls.subMarket.setValue(null);
        this.resetSubmarketDistrictOptions(change);
      }
      if (this.form.controls.district) {
        this.form.controls.district.setValue(null);
      }
    }
    if (change && change.subMarket !== undefined) {
      if (this.form.controls.district) {
        this.resetSubmarketDistrictOptions(change);
        this.form.controls.district.setValue(null);
      }
    }
    if (change && change.periodType != undefined && this.form.controls.periodRange) {
      const isYearly = change.periodType === QuarterSelectorType.Yearly;
      const today = new Date();
      if (isYearly) {
        today.setFullYear(today.getFullYear() - 1);
      }
      this.maxValue = today;
      const maxYear = today.getFullYear();
      this.periodType.set(change.periodType.toString());
      const val = this.form.controls.periodRange.value;
      if (val.eq) {
        val.eq.periodValue = 1;
        if (isYearly && val.eq.periodYear > maxYear) {
          val.eq.periodYear = maxYear;
        }
      }
      if (val.gte) {
        val.gte.periodValue = 1;
        if (isYearly && val.gte.periodYear > maxYear) {
          val.gte.periodYear = maxYear;
        }
      }
      if (val.lte) {
        val.lte.periodValue = 1;
        if (isYearly && val.lte.periodYear > maxYear) {
          val.lte.periodYear = maxYear;
        }
      }
      this.periodRangeValue = val;
      this.form.controls.periodRange.setValue(val);
    }
    this.applyCascadeChanges(change);
    this.change.emit(change);
  }
  /**
   * Handle cascade changes
   * For example, when 'Retail Sector' change, 'Retail Subsector' and 'Segment' should be reset
   * When 'Retail Subsector' change, 'Segment' should be reset
   * @param change
   */
  applyCascadeChanges(change: { [key: string]: any }) {
    const fromControllers = this.form.controls;
    const changeKeys = Object.keys(change);
    const currentFormControlName = changeKeys.find(key => fromControllers.hasOwnProperty(key));
    const parentFilter = this.filters?.find(filter => filter.field === currentFormControlName) as
      | InputProps
      | undefined;
    const targetField = parentFilter?.targetField || null;
    if (targetField) {
      parentFilter.value = change[currentFormControlName];
      const targetFormControl = fromControllers[targetField];
      if (targetFormControl) {
        const targetFilter = this.filters.find(filter => filter.field === targetField) as InputProps | undefined;
        const controlOptions = targetFilter?.controlOptions || null;
        if (controlOptions && controlOptions.lookupCategory && controlOptions.applyCascadeChanges) {
          controlOptions.applyCascadeChanges(parentFilter, targetFilter, this);
        }
      }
    }
  }
  getServices() {
    return {
      lookupService: this.lookupService,
      // Kept for shape compatibility only — no `getServices()` consumer reads this key
      // (`view-spaces-from-tim/filters.ts` and `enquiry-filters.util.ts`, the only two, use
      // `lookupService`). Now the narrow TIM_FILTERS_SINK, which under the host IS the same
      // `ViewSpacesFromTimService` instance as before.
      viewSpacesFromTimService: this.timFiltersSink,
      simpleFormGroupService: this.simpleFormGroupService,
      notificationService: this.notificationService,
      translationService: this.translationService,
      userInfoResolver: this.userInfoResolver,
    };
  }
  public getFilters() {
    return this.filters;
  }
  resetSubmarketDistrictOptions(change) {
    const subMarket = this.filters.find(filter => filter.field === 'subMarket');
    const district = this.filters.find(filter => filter.field === 'district');
    if (change.markets && subMarket) {
      subMarket.value = null;
      subMarket.optionsEndpoint = this.simpleFormGroupService.getGeoMarkets('submarkets', change.markets);
      if (district) {
        district.value = null;
        district.optionsEndpoint = of(null);
      }
    }
    if (change.subMarket !== undefined && district) {
      district.value = null;
      district.optionsEndpoint = change.subMarket
        ? this.simpleFormGroupService.getGeoMarkets('districts', change.subMarket)
        : of(null);
    }
  }
  get inputs(): Array<InputProps<T>> {
    return this.filters.filter(({ type }) => type !== FilterInputType.Separator);
  }
  onSubmit() {
    this.updateView.emit(this.form);
  }
  /**
   * Load persisted filters from IndexedDB for the current user
   */
  private async loadPersistedFilters(): Promise<void> {
    try {
      if (this.indexedDbService.isSupported()) {
        const persistedFilters = await this.indexedDbService.getFilters(
          INDEXED_DB_CONSTANTS.MATCHING_AVAILABILITIES_FILTERS_KEY
        );
        if (persistedFilters?.length) {
          this.userSelectedFiltrs = persistedFilters;
          // Actually apply the persisted filters to the UI
          this.applyPersistedFiltersToUI(persistedFilters);
        }
      }
    } catch (error) {
      // Continue without crashing the app
    }
  }
  /**
   * Apply persisted filters to the UI by reusing populateFormWithFilters logic
   */
  private applyPersistedFiltersToUI(persistedFilters: DataObject[]): void {
    // Only apply persisted filters for specific component types
    if (this.type() !== this.filterTypes.availFromTim) {
      return;
    }
    try {
      // Reuse the existing populateFormWithFilters logic but skip saving to IndexedDB
      this.populateFormWithFilters(persistedFilters, false);
    } catch (error) {
      // Continue without crashing the app
    }
  }
  /**
   * Save filters to IndexedDB
   */
  private async saveFiltersToIndexedDB(filters: DataObject[]): Promise<void> {
    try {
      if (filters?.length && this.indexedDbService.isSupported()) {
        await this.indexedDbService.saveFilters(INDEXED_DB_CONSTANTS.MATCHING_AVAILABILITIES_FILTERS_KEY, filters);
      }
    } catch (error) {
      // Continue without crashing the app
    }
  }
  openFiltersDialog(key: string) {
    if (!this.moreFiltersDialog) {
      // Standalone `nx serve reports-remote` only — see the token's declaration in @miq/platform.
      // Under federation the host's app.config provider resolves, so this never fires.
      console.warn('[filters-bar] MORE_FILTERS_DIALOG is not provided; skipping the more-filters dialog.');
      return;
    }
    //deep copy of filters to avoid modifying the original filters array
    const data = {
      key,
      options: {
        title:
          this.translationService.translate('user.' + key) + ' ' + this.translationService.translate('shared.filters'),
        searchMatchingAvailability: true,
        defaultAvailabilityFilterIds: this.defaultAvailabilityFilterIds(),
      },
      userFilters: this.userSelectedFiltrs, // This now contains persisted filters
      filtersCatalog: this.baseFilters['availability'],
      filterForRecords: true,
      isFilter: true,
    };
    this.notificationService
      .openDialog(this.moreFiltersDialog, data, { panelClass: 'filter-modals' })
      .pipe(
        filterOperator(filterSelections => !!filterSelections),
        map(val => {
          // Filter and return only items that have filters property
          const userSelectedFilters = val.filter(filterItem => filterItem.filters);
          return filtersSieve(userSelectedFilters);
        })
      )
      .subscribe(userSelectedFilters => {
        this.populateFormWithFilters(userSelectedFilters);
      });
  }
  populateFormWithFilters(userSelectedFilters, saveToIndexedDB: boolean = true) {
    this.userSelectedFiltrs = JSON.parse(JSON.stringify(userSelectedFilters));
    // Save filters to IndexedDB for persistence only if requested
    if (saveToIndexedDB) {
      this.saveFiltersToIndexedDB(this.userSelectedFiltrs);
    }
    // Get only user selected filters that have the filters property
    // Flatten the userSelectedFilters
    userSelectedFilters = userSelectedFilters
      .reduce((a, b) => [...a, ...b?.filters], [])
      .map(filterItem => {
        let result = filterItem;
        result.label = result.label || result.value;
        result.value = '';
        // Postal Code comes from the raw filters catalog shaped as a column definition
        // (type: 'multi') rather than an already-resolved FilterInputType, so the bar's @switch
        // on props.type would otherwise match no case and render nothing. It has no
        // lookupCategory, so treat it as a plain text field rather than a dropdown.
        if (isPostalCodeFilter(result)) {
          result.type = FilterInputType.EqualNoCase;
          result.inputType = 'text';
        }
        if ([FilterInputType.Equal, FilterInputType.Text, FilterInputType.EqualNoCase].includes(result.type)) {
          result.placeholder = Placeholder.Text;
        }
        return result;
      });
    // Find the current filter controls in this.form
    let currentFormControls = Object.keys(this.form.controls);
    // take only the controls after the 9th index
    currentFormControls = currentFormControls.slice(this.fixedFilters().length);
    // Remove the formControl that is already in the form, but not in the userSelectedFilters
    this.addOrRemoveFormControls(currentFormControls, userSelectedFilters);
    //push the new user selected filters to the this.filters array, if already not present
    this.addNewFilters(userSelectedFilters);
    // if the filter not present in userSelectedFilters but its in this.filters then remove from this.filters array
    let dynamicFilters = this.getDynamicFilters();
    const staticFilters = this.getStaticFilters();
    dynamicFilters = dynamicFilters.filter(filterItem => {
      const filterItemIdentifier = this.getPrimaryIdentifier(filterItem);
      return userSelectedFilters.some(existingFilter => {
        const existingFilterIdentifier = this.getPrimaryIdentifier(existingFilter);
        return existingFilterIdentifier === filterItemIdentifier;
      });
    });
    this.filters = [...staticFilters, ...dynamicFilters];
    //update the observable to reflect the new filters
    this.pushFiltersToTim();
    // Emit the selection with the updated form value
    this.selection.emit(this.form.value);
  }
  addOrRemoveFormControls(currentFormControls, userSelectedFilters) {
    const userSelectedFilterFields = userSelectedFilters.map(filterItem => this.getPrimaryIdentifier(filterItem));
    const controlsToRemove = currentFormControls?.filter(
      controlName => !userSelectedFilterFields.includes(controlName)
    );
    // add the formControl that are not in the form
    const controlsToAdd = userSelectedFilters.filter(filterItem => {
      const identifier = this.getPrimaryIdentifier(filterItem);
      return !currentFormControls.includes(identifier);
    });
    //remove the formControl that are not in the userSelectedFilters
    controlsToRemove.forEach(controlName => {
      this.form.removeControl(controlName);
    });
    // Add new filters to the form
    controlsToAdd.forEach(filterItem => {
      this.addNewFormControl(filterItem);
    });
  }
  addNewFormControl(filterItem) {
    const controlName = String(this.getPrimaryIdentifier(filterItem));
    if (filterItem.type === FilterInputType.Range) {
      // Create a FormGroup with 'from' and 'to' controls for range type
      const rangeGroup = this.fb.group({
        from: [filterItem.value?.from || null],
        to: [filterItem.value?.to || null],
      });
      this.form.addControl(controlName, rangeGroup);
    } else {
      // Create a simple FormControl for other types
      this.form.addControl(controlName, this.fb.control(filterItem.value || null));
    }
  }
  addNewFilters(userSelectedFilters) {
    userSelectedFilters.forEach(filterItem => {
      filterItem.isDynamic = true;
      const filterIdentifier = this.getPrimaryIdentifier(filterItem);
      const alreadyExists = this.filters.some(existingFilter => {
        const existingIdentifier = this.getPrimaryIdentifier(existingFilter);
        return existingIdentifier === filterIdentifier;
      });
      if (!alreadyExists) {
        this.filters.push(filterItem);
      }
    });
  }
  onResetFilters() {
    const staticFilters = this.getStaticFilters();
    // this code is to clear stand alone filter values in payload on resetting filters
    const dynamicFilters = this.getDynamicFilters();
    dynamicFilters.forEach(filter => {
      const controlName = this.getPrimaryIdentifier(filter);
      if (controlName && this.form?.controls[controlName]) {
        const control = this.form?.controls[controlName];
        if (control instanceof FormGroup) {
          // Handle range controls (FormGroup with from/to)
          control.patchValue({ from: null, to: null });
        } else {
          // Handle simple controls
          control.setValue(null);
        }
      }
    });
    this.filters = [...staticFilters];
    // Clear userSelectedFiltrs completely and remove from IndexedDB
    this.userSelectedFiltrs = [];
    // Clear filters from IndexedDB
    this.clearFiltersFromIndexedDB();
    this.pushFiltersToTim();
  }
  /**
   * Publish the current filter array to the TIM "matching availabilities" screen.
   *
   * Gated on `type()` for the reason documented on {@link TIM_FILTERS_SINK}: the sink's only
   * subscriber is `ViewSpacesFromTimComponent`, and every other TIM branch in this component and
   * its template is gated the same way. Both callers are already only reachable from the
   * `@if (type() === 'searchAvailFromTim')` block in the template (or from
   * `applyPersistedFiltersToUI`, which early-returns on the same condition), so this is a no-op
   * change for the host and simply makes the restriction explicit.
   */
  private pushFiltersToTim() {
    if (this.type() !== this.filterTypes.availFromTim) {
      return;
    }
    this.timFiltersSink?.updateNewFilters(this.filters);
  }
  onClearFilters() {
    const shouldPreserveFilters = [this.filterTypes.availFromTim, this.filterTypes.enquiryFromAvail].includes(
      this.type()
    );
    // Reset form to initial values
    if (this.form) {
      // Create a clean values object for resetting
      const resetValues = {};
      Object.keys(this.form?.controls).forEach(key => {
        const control = this.form?.controls[key];
        if (shouldPreserveFilters && preserveFilterKeys.includes(key)) {
          resetValues[key] = control.value;
        } else {
          if (control instanceof FormGroup) {
            // Handle range controls (FormGroup with from/to)
            resetValues[key] = { from: null, to: null };
          } else {
            // Handle simple controls
            resetValues[key] = null;
          }
        }
      });
      // Reset the form with clean values
      this.form.patchValue(resetValues);
      // Mark form as pristine and untouched
      this.form.markAsPristine();
      this.form.markAsUntouched();
    }
    // Reset filter values in the filters array
    this.filters = this.filters.map(filterItem => {
      const identifier = this.getPrimaryIdentifier(filterItem);
      if (
        shouldPreserveFilters &&
        (preserveFilterKeys.includes(String(identifier)) || preserveFilterKeyIds.includes(Number(identifier)))
      ) {
        return filterItem;
      } else {
        return {
          ...filterItem,
          value: filterItem.type === FilterInputType.Range ? ({ from: null, to: null } as any) : null,
        };
      }
    });
    // Reset component state variables
    this.marketQtdValue = null;
    this.periodRangeValue = null;
    this.currentQtdValue = null;
    // Clear userSelectedFiltrs but keep persisted filters in IndexedDB
    this.userSelectedFiltrs = [];
    // Optionally emit selection with cleared values
    this.selection.emit(this.form.value);
  }
  getStaticFilters() {
    // first 10 filters are static filters
    return this.filters.slice(0, this.fixedFilters().length);
  }
  getDynamicFilters() {
    //after the 10th index, we will return the dynamic filters
    return this.filters.slice(this.fixedFilters().length);
  }
  /**
   * Clear filters from IndexedDB
   */
  private async clearFiltersFromIndexedDB(): Promise<void> {
    try {
      if (this.indexedDbService.isSupported()) {
        await this.indexedDbService.deleteFilters(INDEXED_DB_CONSTANTS.MATCHING_AVAILABILITIES_FILTERS_KEY);
      }
    } catch (error) {
      // Continue without crashing the app
    }
  }
}
